import React from "react";

import styles from "~/pages/CharacterSheet/widgets/RollResultDisplay/RollResultDisplay.scss";
import { RollResult } from "~/pages/CharacterSheet/types";

export interface RollResultDisplayProps {
  rolls: RollResult[];
  onClearAll: () => void;
  onClearRoll: (id: string) => void;
}

/**
 * RollResultDisplay widget for showing a scrolling feed of dice roll results.
 * Displays multiple rolls in a vertical feed with newest at the top.
 */
const RollResultDisplay: React.FC<RollResultDisplayProps> = ({
  rolls,
  onClearAll,
  onClearRoll,
}) => {
  if (rolls.length === 0) return null;

  return (
    <div className={styles.rollFeed}>
      <div className={styles.feedHeader}>
        <span className={styles.feedTitle}>Roll History</span>
        <button
          type="button"
          className={styles.clearAllBtn}
          onClick={onClearAll}
          title="Clear all rolls"
        >
          Clear All
        </button>
      </div>

      <div className={styles.feedContent}>
        {rolls.map((roll) => (
          <div
            key={roll.id}
            className={`
              ${styles.rollResult} 
              ${roll.isCritFumble ? styles.critFumble : ""} 
              ${roll.isFumble ? styles.fumble : ""}
            `}
          >
            <div className={styles.resultHeader}>
              <span className={styles.resultSkill}>{roll.name} Check</span>
              <button
                type="button"
                className={styles.clearResult}
                onClick={() => onClearRoll(roll.id)}
                aria-label={`Clear ${roll.name} roll`}
              >
                &times;
              </button>
            </div>
            <div className={styles.resultBody}>
              <div className={styles.hitsDisplay}>
                <span className={styles.hitsCount}>{roll.hits}</span>
                <span className={styles.hitsLabel}>
                  {roll.hits === 1 ? "Hit" : "Hits"}
                </span>
                {roll.flatHits !== undefined && roll.flatHits !== 0 && (
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--colorSuccess)', marginTop: '0.25rem' }}>
                    ({roll.hits - roll.flatHits} + {roll.flatHits} hit{Math.abs(roll.flatHits) === 1 ? "" : "s"})
                  </div>
                )}
              </div>
              <div className={styles.diceList}>
                {roll.rolls.map((val, idx) => (
                  <span
                    key={idx}
                    className={`
                      ${styles.die} 
                      ${val >= 5 ? styles.hitDie : ""} 
                      ${val === 1 ? styles.oneDie : ""}
                    `}
                  >
                    {val}
                  </span>
                ))}
              </div>
              <div className={styles.resultStatus}>
                {roll.isCritFumble && (
                  <span className={styles.critText}>CRITICAL FUMBLE!</span>
                )}
                {roll.isFumble && <span className={styles.fumbleText}>Fumble</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RollResultDisplay;
